import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, capitalize, FCX } from "@datassential/platform-ui-lib";
import { toast } from "react-toastify";
import cn from "classnames";
import http from "@core/fetch";
import { CategoryOverview } from "@apiHooks/useCategoryProfileDetails";
import useComponentVisible from "@hooks/useComponentVisible";
import Checkbox from "@components/new/Checkbox/Checkbox";
import Dropdown from "@components/new/Dropdown/Dropdown";
import SmallLoader from "@components/SmallLoader/SmallLoader";
import { IconDownloadOutline, IconArrowLeft } from "@icons";
import "./CategoryProfileExport.styles.scss";

interface Props {
    categoryName: string;
    reports: CategoryOverview[];
    isCollapsed?: boolean;
}

const CategoryProfileExport: FCX<Props> = ({ categoryName, reports, isCollapsed = false }) => {
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => setSelectedIds([]), [reports]);

    const isAllSelected = selectedIds.length === 0;
    const hasSingleReport = reports.length === 1;

    const targetReports = useMemo(
        () => (isAllSelected ? reports : reports.filter(report => selectedIds.includes(report.id))),
        [isAllSelected, reports, selectedIds],
    );

    const download = useCallback(async () => {
        if (isDownloading || targetReports.length === 0) return;
        setIsDownloading(true);
        toast.info('Downloading...');
        try {
            if (targetReports.length === 1) {
                const [report] = targetReports;
                const url = http.getQuery('get category overview report', {
                    id: report.id,
                    name: report.encryptedName,
                    typeId: 1,
                });
                await http.downloadFileXHRFromUrl('GET', url);
            } else {
                const body = {
                    categoryName,
                    reports: targetReports.map(({ id, name }) => ({ id, name })),
                };
                await http.downloadFileXHR('POST', 'download category overview reports', {}, JSON.stringify(body));
            }
            toast.success('Report downloaded');
        } catch {
            toast.error('Download failed');
        } finally {
            setIsDownloading(false);
        }
    }, [isDownloading, categoryName, targetReports]);

    const toggleReport = useCallback((id: number) => {
        setSelectedIds(prev => prev.includes(id)
            ? prev.filter(reportId => reportId !== id)
            : [...prev, id]);
    }, []);

    const selectAll = useCallback(() => setSelectedIds([]), []);

    if (reports.length === 0) return null;

    const buttonClassName = cn("CategoryProfileExport__button", isCollapsed && "is-collapsed");

    if (hasSingleReport) {
        return (
            <Button
                className={buttonClassName}
                onClick={download}
                isDisabled={isDownloading}
                testId="buttonCategoryProfileExport"
            >
                {isDownloading ? (
                    <span className="CategoryProfileExport__button-loader">
                        <SmallLoader />
                    </span>
                ) : (
                    <IconDownloadOutline className="CategoryProfileExport__button-icon" />
                )}
                {!isCollapsed && <span className="CategoryProfileExport__button-label">Download</span>}
            </Button>
        );
    }

    return (
        <div className={cn("CategoryProfileExport", isCollapsed && "is-collapsed")} ref={ref}>
            <Button
                className={buttonClassName}
                onClick={() => setIsComponentVisible(prev => !prev)}
                testId="buttonCategoryProfileExport"
            >
                <IconDownloadOutline className="CategoryProfileExport__button-icon" />
                {!isCollapsed && (
                    <>
                        <span className="CategoryProfileExport__button-label">Download</span>
                        <IconArrowLeft
                            className={cn(
                                "CategoryProfileExport__button-arrow",
                                isComponentVisible && "is-open",
                            )}
                        />
                    </>
                )}
            </Button>

            <Dropdown shown={isComponentVisible} position="left">
                <div className="CategoryProfileExport__dropdown" data-testid="blockCategoryProfileExportDropdown">
                    Overviews
                    <div className="CategoryProfileExport__list">
                        <Checkbox
                            className="CategoryProfileExport__option"
                            label="All Overviews"
                            isChecked={isAllSelected}
                            onChange={selectAll}
                        />
                        {reports.map(report => (
                            <Checkbox
                                key={report.id}
                                className="CategoryProfileExport__option"
                                label={capitalize(report.name ?? "")}
                                isChecked={!isAllSelected && selectedIds.includes(report.id)}
                                onChange={() => toggleReport(report.id)}
                            />
                        ))}
                    </div>

                    <Button
                        className="CategoryProfileExport__download"
                        onClick={download}
                        isDisabled={isDownloading || targetReports.length === 0}
                        testId="buttonCategoryProfileExportDownload"
                    >
                        {isDownloading && (
                            <span className="CategoryProfileExport__button-loader">
                                <SmallLoader />
                            </span>
                        )}
                        Download
                    </Button>
                </div>
            </Dropdown>
        </div>
    );
};

export default CategoryProfileExport;
